/* eslint-disable react/no-unescaped-entities */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, Wallet, Receipt, ClipboardList, ArrowLeft, Search,
  RefreshCw, Snowflake, ShieldOff, ShieldCheck, Plus, Minus, X, LogOut,
  PhoneCall, LifeBuoy, Edit2, Save,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { gbp, fmtDate } from "@/lib/wallet";
import SEO from "@/components/site/SEO";

const TABS = [
  { id: "users", label: "Users", icon: Users },
  { id: "calls", label: "Calls", icon: PhoneCall },
  { id: "payments", label: "Payments", icon: Receipt },
  { id: "rates", label: "Rates", icon: PhoneCall },
  { id: "tickets", label: "Tickets", icon: LifeBuoy },
  { id: "audit", label: "Audit log", icon: ClipboardList },
];

export default function AdminV1() {
  const { user, logout, api } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("users");
  const [metrics, setMetrics] = useState(null);

  const loadMetrics = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/metrics");
      setMetrics(data);
    } catch (err) {
      console.error("Could not load admin metrics", err);
    }
  }, [api]);

  useEffect(() => { loadMetrics(); }, [loadMetrics]);

  const onLogout = async () => { await logout(); nav("/admin/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-brand-bgSoft text-brand-ink" data-testid="admin-v1">
      <SEO title="Admin · OneDial" path="/admin/v1" />
      <header className="bg-white border-b border-brand-line sticky top-0 z-30">
        <div className="container-page flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/admin" data-testid="adminv1-back-classic" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/80 hover:text-brand-ink">
              <ArrowLeft className="h-4 w-4" /> Submissions view
            </Link>
            <span className="hidden sm:inline text-xs text-brand-muted">/</span>
            <span className="hidden sm:inline text-sm font-semibold">Admin V1</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-brand-muted hidden md:inline">{user?.email}</span>
            <button type="button" onClick={onLogout} data-testid="adminv1-logout"
              className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2 text-xs font-semibold text-brand-ink hover:bg-brand-bgSoft">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-page py-8 space-y-6">
        {/* Metric tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="adminv1-metrics">
          <Tile label="Customers" value={metrics?.customers?.total ?? "…"} sub={`${metrics?.customers?.verified ?? 0} verified`} />
          <Tile label="Wallet float" value={metrics ? gbp(metrics.wallets.total_balance) : "…"} sub="Total GBP held" />
          <Tile label="Paid top-ups" value={metrics?.payments?.paid_count ?? "…"} sub={metrics ? gbp(metrics.payments.paid_total) : ""} />
          <Tile label="Admin" value={user?.email ? "Online" : "—"} sub={user?.email || ""} valueClass="text-brand-greenDark" />
        </div>

        {/* Tabs */}
        <div className="bg-white border border-brand-line rounded-2xl">
          <div className="border-b border-brand-line px-4 flex flex-wrap gap-1">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`adminv1-tab-${id}`}
                className={`relative inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 ${tab === id ? "border-brand-green text-brand-ink" : "border-transparent text-brand-muted hover:text-brand-ink"}`}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
          <div className="p-4">
            {tab === "users" && <UsersTab api={api} reloadMetrics={loadMetrics} />}
            {tab === "calls" && <CallsTab api={api} />}
            {tab === "payments" && <PaymentsTab api={api} />}
            {tab === "rates" && <RatesTab api={api} />}
            {tab === "tickets" && <TicketsTab api={api} />}
            {tab === "audit" && <AuditTab api={api} />}
          </div>
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, sub, valueClass = "" }) {
  return (
    <div className="rounded-2xl border border-brand-line bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-bold">{label}</p>
      <p className={`mt-1 font-display font-black text-2xl ${valueClass}`}>{value}</p>
      {sub && <p className="text-xs text-brand-muted">{sub}</p>}
    </div>
  );
}

/* ----------------------------- USERS TAB ----------------------------- */
function UsersTab({ api, reloadMetrics }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // user_id of opened drawer

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users?limit=50${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setRows(data.items || []);
    } catch {
      toast.error("Could not load users");
    } finally {
      setLoading(false);
    }
  }, [api, q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by email or name"
            data-testid="adminv1-users-search"
            className="w-full rounded-full border border-brand-line bg-white pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-brand-green"
          />
        </div>
        <button onClick={load} className="rounded-full border border-brand-line px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="adminv1-users-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-brand-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-sm text-brand-muted">No users found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-brand-muted">
              <tr>
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Country</th>
                <th className="py-2 pr-3">Verified</th>
                <th className="py-2 pr-3">Wallet</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Joined</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-line">
              {rows.map((u) => (
                <tr key={u.id} className="hover:bg-brand-bgSoft" data-testid={`adminv1-user-row-${u.id}`}>
                  <td className="py-2 pr-3">
                    <p className="font-semibold">{u.name}</p>
                    <p className="text-xs text-brand-muted">{u.email}</p>
                  </td>
                  <td className="py-2 pr-3 text-xs">{u.country || "—"}</td>
                  <td className="py-2 pr-3 text-xs">{u.email_verified ? <span className="text-emerald-600">Yes</span> : <span className="text-rose-600">No</span>}</td>
                  <td className="py-2 pr-3 font-semibold">{gbp(u.wallet?.balance)}</td>
                  <td className="py-2 pr-3">
                    {u.wallet?.status === "frozen" ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-700 px-2 py-0.5"><Snowflake className="h-3 w-3" /> Frozen</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5">Active</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs text-brand-muted">{fmtDate(u.created_at)}</td>
                  <td className="py-2 pr-3 text-right">
                    <button onClick={() => setSelected(u.id)} data-testid={`adminv1-user-open-${u.id}`}
                      className="rounded-full bg-brand-ink text-white px-3 py-1.5 text-xs font-semibold hover:bg-brand-ink/90">
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <UserDrawer api={api} userId={selected} onClose={() => { setSelected(null); load(); reloadMetrics(); }} />
      )}
    </div>
  );
}

/* ----------------------------- USER DRAWER ----------------------------- */
function UserDrawer({ api, userId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);

  const [adjAmount, setAdjAmount] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: t }] = await Promise.all([
        api.get(`/admin/users/${userId}`),
        api.get(`/admin/users/${userId}/transactions?limit=25`),
      ]);
      setDetail(d);
      setTxns(t.items || []);
    } catch {
      toast.error("Could not load user details");
    } finally {
      setLoading(false);
    }
  }, [api, userId]);

  useEffect(() => { load(); }, [load]);

  async function adjust(direction) {
    const amount = parseFloat(adjAmount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    if (!adjReason.trim() || adjReason.trim().length < 3) { toast.error("Enter a reason"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/wallet/adjust`, {
        direction, amount, reason: adjReason.trim(),
      });
      toast.success(`Wallet ${direction}ed ${gbp(amount)}`);
      setAdjAmount(""); setAdjReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Adjustment failed");
    } finally {
      setBusy(false);
    }
  }

  async function toggleFrozen() {
    const next = detail?.wallet?.status === "frozen" ? "active" : "frozen";
    const reason = window.prompt(`Reason to ${next === "frozen" ? "freeze" : "unfreeze"} this wallet?`);
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/wallet/status`, { status: next, reason: reason.trim() });
      toast.success(`Wallet ${next}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status change failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="adminv1-user-drawer">
      <button className="flex-1 bg-black/40" onClick={onClose} aria-label="Close" />
      <div className="w-full max-w-2xl h-full bg-white overflow-y-auto border-l border-brand-line">
        <div className="sticky top-0 bg-white border-b border-brand-line px-5 py-4 flex items-center justify-between">
          <h3 className="font-display font-extrabold text-lg">User detail</h3>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-brand-bgSoft" data-testid="adminv1-drawer-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {loading || !detail ? (
          <div className="p-6 text-sm text-brand-muted">Loading…</div>
        ) : (
          <div className="p-5 space-y-6">
            <div>
              <p className="label-eyebrow">Profile</p>
              <div className="mt-2 grid grid-cols-2 gap-y-1 text-sm">
                <div className="text-brand-muted">Name</div><div className="font-semibold">{detail.user.name}</div>
                <div className="text-brand-muted">Email</div><div className="font-semibold">{detail.user.email}</div>
                <div className="text-brand-muted">Country</div><div className="font-semibold">{detail.user.country}</div>
                <div className="text-brand-muted">Verified</div><div className="font-semibold">{detail.user.email_verified ? "Yes" : "No"}</div>
                <div className="text-brand-muted">Joined</div><div className="font-semibold">{fmtDate(detail.user.created_at)}</div>
              </div>
            </div>

            <div className="rounded-2xl bg-brand-ink text-white p-5">
              <p className="label-eyebrow text-brand-green">Wallet</p>
              <p className="font-display font-black text-3xl mt-1" data-testid="adminv1-drawer-balance">{gbp(detail.wallet.balance)}</p>
              <div className="mt-3 flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-0.5 ${detail.wallet.status === "frozen" ? "bg-amber-500/20 text-amber-200" : "bg-emerald-500/20 text-emerald-200"}`}>
                  {detail.wallet.status === "frozen" ? <Snowflake className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />} {detail.wallet.status}
                </span>
                <button onClick={toggleFrozen} disabled={busy} data-testid="adminv1-drawer-toggle-freeze"
                  className="ml-auto text-xs font-semibold rounded-full bg-white/10 hover:bg-white/20 px-3 py-1 inline-flex items-center gap-1">
                  {detail.wallet.status === "frozen" ? <><ShieldCheck className="h-3 w-3" /> Unfreeze</> : <><ShieldOff className="h-3 w-3" /> Freeze</>}
                </button>
              </div>
            </div>

            <div>
              <p className="label-eyebrow">Manual adjustment</p>
              <p className="text-xs text-brand-muted mt-1">Credit or debit this wallet. All adjustments are recorded in the audit log.</p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input type="number" min="0.01" step="0.01" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)}
                  placeholder="Amount (GBP)" data-testid="adminv1-drawer-amount"
                  className="rounded-lg border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-green" />
                <input type="text" value={adjReason} onChange={(e) => setAdjReason(e.target.value)}
                  placeholder="Reason (min 3 chars)" data-testid="adminv1-drawer-reason"
                  className="rounded-lg border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-green" />
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => adjust("credit")} disabled={busy} data-testid="adminv1-drawer-credit"
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 px-4 py-2 text-xs font-semibold text-white">
                  <Plus className="h-3.5 w-3.5" /> Credit
                </button>
                <button onClick={() => adjust("debit")} disabled={busy} data-testid="adminv1-drawer-debit"
                  className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 hover:bg-rose-700 px-4 py-2 text-xs font-semibold text-white">
                  <Minus className="h-3.5 w-3.5" /> Debit
                </button>
              </div>
            </div>

            <div>
              <p className="label-eyebrow">Transactions</p>
              <ul className="mt-2 divide-y divide-brand-line text-sm" data-testid="adminv1-drawer-txns">
                {txns.length === 0 && <li className="py-4 text-brand-muted">No transactions.</li>}
                {txns.map((t) => (
                  <li key={t.id} className="py-2 flex items-center gap-3">
                    <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${t.direction === "credit" ? "bg-emerald-100 text-emerald-700" : t.direction === "debit" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>
                      {t.direction}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{t.reason}</p>
                      <p className="text-[11px] text-brand-muted">{fmtDate(t.created_at)} · {t.created_by}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{t.direction === "credit" ? "+" : t.direction === "debit" ? "−" : ""}{gbp(t.amount)}</p>
                      <p className="text-[11px] text-brand-muted">→ {gbp(t.balance_after)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ----------------------------- PAYMENTS TAB ----------------------------- */
function PaymentsTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/payments?limit=100${filter ? `&status=${filter}` : ""}`);
      setRows(data.items || []);
    } catch {
      toast.error("Could not load payments");
    } finally { setLoading(false); }
  }, [api, filter]);

  useEffect(() => { load(); }, [load]);

  const statuses = ["", "paid", "pending", "failed", "expired"];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {statuses.map((s) => (
            <button key={s || "all"} onClick={() => setFilter(s)}
              data-testid={`adminv1-pay-filter-${s || "all"}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${filter === s ? "bg-brand-ink text-white border-brand-ink" : "bg-white border-brand-line text-brand-ink hover:bg-brand-bgSoft"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
        <button onClick={load} className="ml-auto rounded-full border border-brand-line px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="adminv1-pay-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-brand-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-sm text-brand-muted">No payments yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-brand-muted">
              <tr>
                <th className="py-2 pr-3">Created</th>
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Amount</th>
                <th className="py-2 pr-3">Package</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Credited</th>
                <th className="py-2 pr-3">Session</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-line">
              {rows.map((r) => (
                <tr key={r.session_id} data-testid={`adminv1-pay-row-${r.session_id}`}>
                  <td className="py-2 pr-3 text-xs">{fmtDate(r.created_at)}</td>
                  <td className="py-2 pr-3 text-xs">{r.user_email}</td>
                  <td className="py-2 pr-3 font-semibold">{gbp(r.amount)}</td>
                  <td className="py-2 pr-3 text-xs">{r.package_id}</td>
                  <td className="py-2 pr-3">
                    <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${r.status === "paid" ? "bg-emerald-100 text-emerald-700" : r.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-xs">{r.credited ? "Yes" : "No"}</td>
                  <td className="py-2 pr-3 text-[10px] font-mono text-brand-muted truncate max-w-[180px]">{r.session_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ----------------------------- AUDIT TAB ----------------------------- */
function AuditTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/audit?limit=100`);
      setRows(data.items || []);
    } catch {
      toast.error("Could not load audit log");
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // No useMemo needed for a trivial `length` read; React re-renders are
  // already debounced upstream by the `rows` state setter.
  const summary = rows.length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-brand-muted">{summary} entries</span>
        <button onClick={load} className="ml-auto rounded-full border border-brand-line px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="adminv1-audit-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>
      {loading ? (
        <div className="py-12 text-center text-sm text-brand-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-sm text-brand-muted">No admin actions recorded yet.</div>
      ) : (
        <ul className="divide-y divide-brand-line" data-testid="adminv1-audit-list">
          {rows.map((r) => (
            <li key={r.id} className="py-3 flex items-start gap-3">
              <span className="text-xs font-bold uppercase tracking-wider rounded-full bg-brand-bgSoft text-brand-ink px-2 py-0.5 mt-0.5">{r.action}</span>
              <div className="flex-1 min-w-0 text-sm">
                <p className="truncate">Target {r.target_type}: <span className="font-mono text-xs">{r.target_id}</span></p>
                <p className="text-xs text-brand-muted">{fmtDate(r.created_at)} · admin {r.admin_id?.slice(0,8)}</p>
                {r.payload && Object.keys(r.payload).length > 0 && (
                  <pre className="mt-1 text-[11px] text-brand-muted overflow-x-auto">{JSON.stringify(r.payload)}</pre>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ----------------------------- RATES TAB ----------------------------- */
function RatesTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editId, setEditId] = useState(null);
  const [draft, setDraft] = useState({ rate_per_minute: 0, active: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/rates?limit=2000${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setRows(data.items || []);
    } catch { toast.error("Could not load rates"); }
    finally { setLoading(false); }
  }, [api, q]);

  useEffect(() => { load(); }, [load]);

  function beginEdit(r) {
    setEditId(r.id);
    setDraft({ rate_per_minute: r.rate_per_minute, active: r.active });
  }
  async function save(r) {
    try {
      await api.patch(`/admin/rates/${r.id}`, {
        rate_per_minute: parseFloat(draft.rate_per_minute),
        active: !!draft.active,
      });
      toast.success(`Updated ${r.destination}`);
      setEditId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  }

  async function uploadCsv(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/rates/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Inserted ${data.inserted} · Updated ${data.updated} · Errors ${data.errors}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    e.target.value = "";
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search country, destination, prefix" data-testid="adminv1-rates-search"
            className="w-full rounded-full border border-brand-line bg-white pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-brand-green" />
        </div>
        <label className="rounded-full border border-brand-line bg-white hover:bg-brand-bgSoft px-3 py-2 text-xs font-semibold cursor-pointer" data-testid="adminv1-rates-import-label">
          Import CSV
          <input type="file" accept=".csv" onChange={uploadCsv} className="hidden" data-testid="adminv1-rates-import" />
        </label>
        <button onClick={load} className="rounded-full border border-brand-line px-3 py-2 text-xs font-semibold inline-flex items-center gap-1" data-testid="adminv1-rates-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-brand-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-sm text-brand-muted">No rates configured.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-brand-muted">
              <tr>
                <th className="py-2 pr-3">Country</th>
                <th className="py-2 pr-3">Destination</th>
                <th className="py-2 pr-3">Prefix</th>
                <th className="py-2 pr-3">Rate £/min</th>
                <th className="py-2 pr-3">Wholesale</th>
                <th className="py-2 pr-3">Active</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-line">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`adminv1-rate-row-${r.id}`}>
                  <td className="py-2 pr-3 font-semibold">{r.country_name}</td>
                  <td className="py-2 pr-3 text-brand-ink/80">{r.destination}</td>
                  <td className="py-2 pr-3 font-mono text-xs">+{r.prefix}</td>
                  <td className="py-2 pr-3">
                    {editId === r.id ? (
                      <input type="number" min="0" step="0.001" value={draft.rate_per_minute}
                        onChange={(e) => setDraft({...draft, rate_per_minute: e.target.value})}
                        data-testid={`adminv1-rate-edit-${r.id}`}
                        className="w-20 rounded border border-brand-line bg-white px-2 py-1 text-xs" />
                    ) : gbp(r.rate_per_minute)}
                  </td>
                  <td className="py-2 pr-3 text-brand-muted">{r.wholesale_per_minute ? gbp(r.wholesale_per_minute) : "—"}</td>
                  <td className="py-2 pr-3">
                    {editId === r.id ? (
                      <input type="checkbox" checked={!!draft.active}
                        onChange={(e) => setDraft({...draft, active: e.target.checked})}
                        data-testid={`adminv1-rate-active-${r.id}`} />
                    ) : (r.active ? "Yes" : "No")}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {editId === r.id ? (
                      <button onClick={() => save(r)} data-testid={`adminv1-rate-save-${r.id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-brand-green text-white px-3 py-1 text-xs font-semibold">
                        <Save className="h-3 w-3" /> Save
                      </button>
                    ) : (
                      <button onClick={() => beginEdit(r)} data-testid={`adminv1-rate-edit-btn-${r.id}`}
                        className="inline-flex items-center gap-1 rounded-full bg-white border border-brand-line hover:bg-brand-bgSoft px-3 py-1 text-xs font-semibold">
                        <Edit2 className="h-3 w-3" /> Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-brand-muted">CSV header row: country_code, country_name, destination, prefix, rate_per_minute, wholesale_per_minute, billing_increment_seconds, min_duration_seconds, active</p>
    </div>
  );
}

/* ----------------------------- TICKETS TAB ----------------------------- */
function TicketsTab({ api }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/tickets?limit=100${status ? `&status=${status}` : ""}`);
      setRows(data.items || []);
    } catch { toast.error("Could not load tickets"); }
    finally { setLoading(false); }
  }, [api, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {["", "open", "pending", "resolved", "closed"].map((s) => (
            <button key={s || "all"} onClick={() => setStatus(s)}
              data-testid={`adminv1-tickets-filter-${s || "all"}`}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${status === s ? "bg-brand-ink text-white border-brand-ink" : "bg-white border-brand-line text-brand-ink hover:bg-brand-bgSoft"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
        <button onClick={load} className="ml-auto rounded-full border border-brand-line px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="adminv1-tickets-refresh">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-brand-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-sm text-brand-muted">No tickets.</div>
      ) : (
        <ul className="divide-y divide-brand-line">
          {rows.map((t) => (
            <li key={t.id} className="py-3" data-testid={`adminv1-ticket-row-${t.id}`}>
              <button onClick={() => setOpenId(openId === t.id ? null : t.id)} className="w-full text-left">
                <div className="flex items-center gap-3">
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${t.status === "open" ? "bg-emerald-100 text-emerald-700" : t.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>{t.status}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${t.priority === "high" ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-700"}`}>{t.priority}</span>
                  <span className="font-semibold flex-1 truncate">{t.subject}</span>
                  <span className="text-xs text-brand-muted">{fmtDate(t.last_message_at)}</span>
                </div>
                <p className="text-xs text-brand-muted mt-1">{t.user_email}</p>
              </button>
              {openId === t.id && <TicketDrawer api={api} ticketId={t.id} onChange={load} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TicketDrawer({ api, ticketId, onChange }) {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/tickets/${ticketId}`);
      setData(data);
    } catch (err) {
      console.error("Could not load admin ticket", err);
    }
  }, [api, ticketId]);

  useEffect(() => { load(); }, [load]);

  async function send() {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/tickets/${ticketId}/reply`, { body: reply.trim() });
      setReply("");
      await load();
      onChange?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Reply failed"); }
    finally { setBusy(false); }
  }

  async function setStatus(s) {
    setBusy(true);
    try {
      await api.patch(`/admin/tickets/${ticketId}`, { status: s });
      await load();
      onChange?.();
    } catch (err) { toast.error("Status update failed"); }
    finally { setBusy(false); }
  }

  if (!data) return <div className="mt-2 text-sm text-brand-muted">Loading…</div>;

  return (
    <div className="mt-3 rounded-xl border border-brand-line bg-brand-bgSoft p-4" data-testid="adminv1-ticket-drawer">
      <ul className="space-y-2 max-h-80 overflow-y-auto">
        {data.messages.map((m) => (
          <li key={m.id} className={`rounded-lg p-3 text-sm ${m.author_role === "admin" ? "bg-brand-greenSoft/40" : "bg-white border border-brand-line"}`}>
            <p className="text-[11px] font-semibold text-brand-muted">{m.author_role === "admin" ? "OneDial Support" : "Customer"} · {fmtDate(m.created_at)}</p>
            <p className="mt-1 whitespace-pre-wrap">{m.body}</p>
          </li>
        ))}
      </ul>
      <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3}
        placeholder="Type your reply…" data-testid="adminv1-ticket-reply-input"
        className="mt-3 w-full rounded-lg border border-brand-line bg-white px-3 py-2 text-sm" />
      <div className="mt-2 flex flex-wrap gap-2">
        <button onClick={send} disabled={busy} data-testid="adminv1-ticket-reply-send"
          className="rounded-full bg-brand-green hover:bg-brand-greenHover text-white px-4 py-1.5 text-xs font-semibold">
          Send reply
        </button>
        {data.ticket.status !== "resolved" && (
          <button onClick={() => setStatus("resolved")} className="rounded-full bg-white border border-brand-line px-4 py-1.5 text-xs font-semibold">Mark resolved</button>
        )}
        {data.ticket.status !== "closed" && (
          <button onClick={() => setStatus("closed")} className="rounded-full bg-white border border-brand-line px-4 py-1.5 text-xs font-semibold">Close</button>
        )}
      </div>
    </div>
  );
}


/* ----------------------------- CALLS TAB ----------------------------- */
function CallsTab({ api }) {
  const [metrics, setMetrics] = useState(null);
  const [live, setLive] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, l, r] = await Promise.all([
        api.get("/admin/calls/metrics"),
        api.get("/admin/calls/live"),
        api.get("/admin/calls?limit=50"),
      ]);
      setMetrics(m.data);
      setLive(l.data.items || []);
      setRecent(r.data.items || []);
    } catch (err) {
      // Non-fatal: the live admin dashboard simply skips this tick if the
      // network blips. Surface in dev so we can spot persistent failures.
      if (process.env.NODE_ENV !== "production") console.warn("admin metrics poll", err?.message);
    }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // live refresh
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="adminv1-calls-metrics">
        <Tile label="Calls today" value={metrics?.today?.calls ?? "…"} />
        <Tile label="Revenue today" value={metrics ? gbp(metrics.today.revenue_gbp) : "…"} />
        <Tile label="Minutes today" value={metrics?.today?.minutes ?? "…"} />
        <Tile label="Active carrier" value={metrics?.carrier ?? "…"} valueClass="text-brand-greenDark" />
      </div>

      <div className="rounded-2xl border border-brand-line p-4">
        <h3 className="font-display font-extrabold text-base mb-3">Live calls</h3>
        {live.length === 0 ? (
          <p className="text-sm text-brand-muted">No calls in progress.</p>
        ) : (
          <ul className="divide-y divide-brand-line text-sm" data-testid="adminv1-live-calls">
            {live.map((c) => (
              <li key={c.id} className="py-2 flex items-center gap-3">
                <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[11px] font-semibold">{c.status}</span>
                <span className="font-mono text-xs">{c.to_e164}</span>
                <span className="text-xs text-brand-muted">{c.destination?.country_name}</span>
                <span className="ml-auto text-xs">{c.duration_seconds || 0}s · {gbp((c.cost_minor || 0)/100)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-2xl border border-brand-line p-4">
        <h3 className="font-display font-extrabold text-base mb-3">Top destinations (lifetime)</h3>
        {(metrics?.top_destinations || []).length === 0 ? (
          <p className="text-sm text-brand-muted">No completed calls yet.</p>
        ) : (
          <ul className="divide-y divide-brand-line text-sm">
            {metrics.top_destinations.map((d) => (
              <li key={d.country} className="py-2 flex items-center gap-3">
                <span className="font-semibold">{d.country}</span>
                <span className="ml-auto text-xs">{d.calls} calls · {gbp(d.revenue)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-2xl border border-brand-line p-4">
        <h3 className="font-display font-extrabold text-base mb-3">Recent CDRs</h3>
        {loading ? <p className="text-sm text-brand-muted">Loading…</p> : recent.length === 0 ? (
          <p className="text-sm text-brand-muted">No call records yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-brand-muted">
                <tr>
                  <th className="py-2 pr-3">When</th><th className="py-2 pr-3">User</th>
                  <th className="py-2 pr-3">To</th><th className="py-2 pr-3">Country</th>
                  <th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Duration</th>
                  <th className="py-2 pr-3 text-right">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-line">
                {recent.map((c) => (
                  <tr key={c.id}>
                    <td className="py-2 pr-3 text-xs">{fmtDate(c.created_at)}</td>
                    <td className="py-2 pr-3 text-xs">{c.user_email}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{c.to_e164}</td>
                    <td className="py-2 pr-3 text-xs">{c.destination?.country_name}</td>
                    <td className="py-2 pr-3 text-xs">{c.status}</td>
                    <td className="py-2 pr-3 text-xs">{c.duration_seconds || 0}s</td>
                    <td className="py-2 pr-3 text-right font-semibold">{gbp((c.cost_minor || 0)/100)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

